import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api, fmtDate, dollars } from "@/lib/api";
import { toast } from "sonner";
import { User, Bell, Calendar, MessageSquare, Star } from "lucide-react";

export default function VendorDashboard() {
    const nav = useNavigate();
    const [apps, setApps] = useState([]);
    const [profile, setProfile] = useState(null);
    const [notifs, setNotifs] = useState([]);
    const [paying, setPaying] = useState(null);
    const [myReviews, setMyReviews] = useState([]);

    const load = async () => {
        const [a, p, n, r] = await Promise.all([
            api.get("/vendor/applications"),
            api.get("/vendor/profile"),
            api.get("/notifications"),
            api.get("/vendor/reviews"),
        ]);
        setApps(a.data); setProfile(p.data); setNotifs(n.data); setMyReviews(r.data);
    };

    useEffect(() => { load(); }, []);

    const reportReview = async (reviewId) => {
        const reason = window.prompt("What's wrong with this review? Admin will be notified.", "");
        if (!reason || reason.trim().length < 3) return;
        try {
            await api.post(`/vendor/reviews/${reviewId}/report`, { reason });
            toast.success("Reported. Admin will follow up.");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Report failed"); }
    };

    const pay = async (appId) => {
        setPaying(appId);
        try {
            const res = await api.post(`/payments/checkout-session/${appId}`, { origin_url: window.location.origin });
            window.location.href = res.data.session_url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout");
            setPaying(null);
        }
    };

    return (
        <Shell>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="label-base">Vendor</div>
                        <h1 className="font-heading text-3xl md:text-4xl font-extrabold">{profile?.business_name || "Your booth"}</h1>
                    </div>
                    <Link to="/vendor/profile" data-testid="vendor-edit-profile" className="pill px-5 py-3 font-semibold border border-brand-line bg-white hover:bg-brand-bone inline-flex items-center gap-2">
                        <User size={16} /> Edit profile
                    </Link>
                </div>

                <div className="mt-8 grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 rounded-xl border border-brand-line bg-white p-6">
                        <h3 className="font-heading font-bold flex items-center gap-2"><Calendar size={16} className="text-brand-coral" /> Your applications</h3>
                        {apps.length === 0 ? (
                            <div className="mt-4 text-sm text-brand-mute">
                                You haven't applied to any events yet.&nbsp;
                                <Link to="/marketplace" className="text-brand-forest font-semibold hover:underline">Browse the marketplace →</Link>
                            </div>
                        ) : (
                            <ul className="mt-4 divide-y divide-brand-line">
                                {apps.map((a) => (
                                    <li key={a.id} className="py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`vendor-app-${a.id}`}>
                                        <div>
                                            <Link to={`/events/${a.event?.id}`} className="font-heading font-bold hover:text-brand-forest">{a.event?.name}</Link>
                                            <div className="text-xs text-brand-mute mt-0.5">{fmtDate(a.event?.event_date)} · {a.event?.city || a.event?.location}</div>
                                            <div className="text-xs mt-1">Status: <span className="font-semibold">{a.status.replace(/_/g, " ")}</span></div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {a.fee_cents > 0 && <span className="font-bold text-brand-forest">{dollars(a.fee_cents)}</span>}
                                            {a.status === "approved_pending_payment" && (
                                                <button data-testid={`pay-${a.id}`} disabled={paying === a.id} onClick={() => pay(a.id)} className="pill btn-coral px-4 py-2 text-sm font-semibold disabled:opacity-50">
                                                    {paying === a.id ? "Redirecting…" : "Confirm booth"}
                                                </button>
                                            )}
                                            {a.status === "paid" && a.event?.host_id && (
                                                <button data-testid={`msg-host-${a.id}`}
                                                    onClick={async () => {
                                                        try {
                                                            await api.post("/conversations/start", { event_id: a.event.id, with_user_id: a.event.host_id });
                                                            nav("/inbox");
                                                        } catch (err) { toast.error(err?.response?.data?.detail || "Could not open conversation"); }
                                                    }}
                                                    className="pill px-3 py-1.5 text-xs font-semibold border border-brand-forest/30 text-brand-forest bg-white hover:bg-brand-mint inline-flex items-center gap-1">
                                                    <MessageSquare size={12} /> Message host
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="rounded-xl border border-brand-line bg-white p-6">
                        <h3 className="font-heading font-bold flex items-center gap-2"><Bell size={16} className="text-brand-coral" /> Notifications</h3>
                        {notifs.length === 0 ? (
                            <p className="text-sm text-brand-mute mt-4">Nothing yet. You'll see invites, approvals, and event updates here.</p>
                        ) : (
                            <ul className="mt-4 space-y-3 max-h-96 overflow-auto">
                                {notifs.map((n) => (
                                    <li key={n.id} className={`rounded-lg p-3 ${n.read ? "bg-brand-bone" : "bg-brand-mint"}`}>
                                        <div className="text-sm font-bold">{n.title}</div>
                                        <div className="text-xs text-brand-mute mt-0.5">{n.body}</div>
                                        {n.link && <Link to={n.link} className="text-xs font-semibold text-brand-forest hover:underline mt-1 inline-block">Open →</Link>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Your reviews — vendor-side view of all reviews (any moderation status).
                    No edit/delete; vendors can only Report. */}
                {myReviews.length > 0 && (
                    <div className="mt-8 rounded-xl border border-brand-line bg-white p-6" data-testid="vendor-my-reviews">
                        <h3 className="font-heading font-bold flex items-center gap-2"><Star size={16} className="text-amber-500" /> Your reviews</h3>
                        <p className="text-xs text-brand-mute mt-1">Reviews from hosts who confirmed you for past events. Only approved reviews appear publicly on your profile.</p>
                        <ul className="mt-4 divide-y divide-brand-line">
                            {myReviews.map((r) => (
                                <li key={r.id} className="py-3" data-testid={`vendor-review-${r.id}`}>
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-2 text-amber-500">
                                            {Array.from({ length: r.stars }).map((_, i) => (<Star key={i} size={14} className="fill-amber-400" />))}
                                            {Array.from({ length: 5 - r.stars }).map((_, i) => (<Star key={`e${i}`} size={14} className="text-brand-line" />))}
                                            <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "hidden" ? "bg-slate-100 text-slate-700" : r.status === "flagged" ? "bg-amber-100 text-amber-700" : "bg-brand-bone text-brand-mute"}`}>{r.status}</span>
                                        </div>
                                        <div className="text-xs text-brand-mute">{r.event?.name} • {r.event?.event_date}</div>
                                    </div>
                                    {r.text && <p className="text-sm text-brand-ink/85 mt-2 whitespace-pre-line">{r.text}</p>}
                                    {(r.tags || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {r.tags.map((t) => <span key={t} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-bone text-brand-ink">{t}</span>)}
                                        </div>
                                    )}
                                    <button data-testid={`vendor-report-review-${r.id}`} onClick={() => reportReview(r.id)} className="mt-2 text-xs text-brand-coral hover:underline font-semibold">Report this review</button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </Shell>
    );
}
